import PropTypes from 'prop-types';
import { Component } from 'react';

export const MEDIA_QUERIES = {
  desktop: { minWidth: 1221 },
  tablet: { minWidth: 761, maxWidth: 1220 },
  notMobile: { minWidth: 761 },
  mobile: { maxWidth: 760 },
  notDesktop: { maxWidth: 1220 }
};

export const MEDIA_QUERY_STRINGS = {
  desktop: '(min-width: 1221px)',
  tablet: '(min-width: 761px) and (max-width: 1220px)',
  notMobile: '(min-width: 761px)',
  mobile: '(max-width: 760px)',
  notDesktop: '(max-width: 1220px)'
};

/*
 * Designed to address a bug on zoomed-in browser windows.
 * Details: http://damienclarke.me/code/posts/those-1px-gaps-between-media-queries-can-be-a-problem
 *
 * Why don't we just modify the MEDIA_QUERY_STRINGS object directly? That object
 * is exported and used for other functionality throughout the rest of the app,
 * and the rest of the app should not be aware of this hack to the breakpoint.
 */
const ZOOM_FIX_MEDIA_QUERY_STRINGS = {
  desktop: '(min-width: 1221px)',
  tablet: '(min-width: 761px) and (max-width: 1220.98px)',
  notMobile: '(min-width: 761px)',
  mobile: '(max-width: 760.98px)',
  notDesktop: '(max-width: 1220.98px)'
};

export default class MediaQuery extends Component {
  /*
   * On initial `render`, both the client and the server will
   * output the markup for the server-side guessed breakpoint.
   */
  state = {
    shouldRenderChildren: this.initialShouldRenderChildren()
  };

  /*
   * The MQL object is responsible for updating the state as the
   * user resizes the window.
   *
   * `setupMql` also triggers that state update immediately, because
   * the initial render was using the server-side guessed breakpoint rather
   * than the actual breakpoint.
   */
  componentDidMount() {
    this.setupMql(this.props.breakpoint);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.breakpoint !== nextProps.breakpoint) {
      this.teardownMql();
      this.setupMql(nextProps.breakpoint);
    }
  }

  componentWillUnmount() {
    this.teardownMql();
  }

  /*
   * This is used on the server render, and on the _initial_ render on the
   * client. Its only inputs are the guessed breakpoint and props.breakpoint.
   */
  initialShouldRenderChildren() {
    const guessedBreakpoint = this.context.guessedBreakpoint || 'desktop';
    switch (this.props.breakpoint) {
      case guessedBreakpoint:
        return true;
      case 'notMobile':
        return ['desktop', 'tablet'].includes(guessedBreakpoint);
      case 'notDesktop':
        return ['tablet', 'mobile'].includes(guessedBreakpoint);
    }
    return false;
  }

  setupMql(breakpoint) {
    const mediaQuery = ZOOM_FIX_MEDIA_QUERY_STRINGS[breakpoint];
    this.mql = window.matchMedia(mediaQuery);
    this.mql.addListener(this.mqlListener);
    this.mqlListener(this.mql);
  }

  mqlListener = (mql) => {
    if (mql.matches && !this.state.shouldRenderChildren) {
      this.setState({ shouldRenderChildren: true });
    }
    if (!mql.matches && this.state.shouldRenderChildren) {
      this.setState({ shouldRenderChildren: false });
    }
  };

  teardownMql() {
    this.mql.removeListener(this.mqlListener);
    delete this.mql;
  }

  render() {
    return this.state.shouldRenderChildren ? this.props.children : null;
  }
}

MediaQuery.propTypes = {
  breakpoint: PropTypes.oneOf([
    'desktop',
    'tablet',
    'mobile',
    'notDesktop',
    'notMobile'
  ]).isRequired,
  children: PropTypes.node.isRequired
};

MediaQuery.contextTypes = {
  guessedBreakpoint: PropTypes.oneOf([
    'desktop',
    'tablet',
    'mobile'
  ])
};
